import { instanceToPlain, plainToInstance } from 'class-transformer';
import { collection, doc, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Condominium, Section } from '../../entities';
import { Collections, db, functions } from '../../utils/firebase.config';
import { converterOf } from '../../utils/firebase.utils';
import CreateSectionInput from './resources/create-section.resource';
import UpdateSectionInput from './resources/update-section.resource';

export const useSections = (condominiumId: string) => {
  const condominiumsCollection = collection(db, Collections.Condominiums).withConverter(converterOf(Condominium));
  const condominiumsDoc = doc(condominiumsCollection, condominiumId);

  const sectionsCollection = collection(condominiumsDoc, Collections.Sections).withConverter(converterOf(Section));

  const fetchAll = async (): Promise<Section[]> => {
    const response = await getDocs<Section>(sectionsCollection);

    return response.docs.map((sectionDoc) => sectionDoc.data());
  };

  const create = async (input: Omit<Section, 'id' | 'condominiumId'>): Promise<Section> => {
    const createSection = httpsCallable(functions, 'insertCondominiumSection');

    const newSection = new CreateSectionInput({
      ...input,
      condominiumId,
    });

    const { data } = await createSection(instanceToPlain(newSection));

    return plainToInstance(Section, data);
  };

  const update = async ({ id, label }: Partial<Section> & { id: string }): Promise<void> => {
    const updateSection = httpsCallable(functions, 'updateCondominiumSection');

    const data = new UpdateSectionInput({ id, condominiumId, label });

    await updateSection(instanceToPlain(data));
  };

  return { fetchAll, create, update };
};

export const lazyFetchSections = async (condominiumId: string): Promise<Section[]> => {
  const condominiumsCollection = collection(db, Collections.Condominiums).withConverter(converterOf(Condominium));
  const condominiumsDoc = doc(condominiumsCollection, condominiumId);

  const sectionsCollection = collection(condominiumsDoc, Collections.Sections).withConverter(converterOf(Section));
  const response = await getDocs(sectionsCollection);

  return response.docs.map((section) => section.data());
};

export default useSections;
